import type DropdownSubmenu from '../dropdown-submenu/DropdownSubmenu.js'
import type Popout from '../popout/Popout.js'
import { html, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'

import { FloatingMixin } from '../common/mixins/FloatingComponentMixin.js'
import componentStyle from '../common/styles/Component.css'

import style from './Dropdown.css'
import '../popout/Popout.js'
import '../icon/Icon.js'
import '../dropdown-item/DropdownItem.js'

/**
 * Dropdown menu displays a list of actions or selectable options for
 * a user. Dropdown uses popout component internally to create
 * the overlay functionality.
 *
 * @status ready
 * @category action
 *
 * @slot - The dropdown content.
 * @slot toggle - Used to place the toggle for dropdown.
 * @slot header - Optional slot that holds a header for the dropdown.
 * @slot header-end - Optional slot that positions content at the end of the header. Useful for actions or additional info.
 *
 * @fires {NordEvent} open - Dispatched when the popout is opened.
 * @fires {NordEvent} close - Dispatched when the popout is closed.
 *
 * @cssprop [--n-dropdown-size=250px] - Controls the inline size, or width, of the dropdown. Will resize up to 1.5 times to account for larger content.
 * @cssprop [--n-dropdown-max-block-size=460px] - Controls the maximum block size, or height, of the dropdown. Larger content will get a scrollbar.
 */
@customElement('nord-dropdown')
export default class Dropdown extends FloatingMixin(LitElement) {
  static styles = [componentStyle, style]

  /**
   * we delegate focus, to ensure focus does not move to body if you click
   * some whitespace or a dropdown-group heading, as this would close the dropdown
   * @internal
   */
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }

  private headerSlot = new SlotController(this, 'header')
  private headerEndSlot = new SlotController(this, 'header-end')

  @query('.n-dropdown-content') private list!: HTMLElement
  @query('nord-popout', true) private popout!: Popout

  @state() private submenuStack: DropdownSubmenu[] = []
  private isNavigating = false

  private get isShowingSubmenu(): boolean {
    return this.submenuStack.length > 0
  }

  /**
   * @internal
   */
  get submenuStackRef(): readonly DropdownSubmenu[] {
    return this.submenuStack
  }

  /**
   * @internal
   */
  get deepestSubmenu(): DropdownSubmenu | undefined {
    return this.submenuStack[this.submenuStack.length - 1]
  }

  /**
   * Controls whether the toggle slot expands to fill the width of its container.
   */
  @property({ reflect: true, type: Boolean }) expand = false

  /**
   * The size of the dropdown. This affects the minimum and maximum inline-size
   * of the dropdown.
   */
  @property({ reflect: true }) size: 's' | 'm' | 'l' = 'm'

  /**
   * Set to true to always display the dropdown as a floating overlay, even on smaller viewports.
   */
  @property({ reflect: true, type: Boolean, attribute: 'always-floating' })
  alwaysFloating: boolean = false

  @observe('alwaysFloating')
  protected handleAlwaysFloatingChange() {
    if (this.open) {
      this.open = false
      this.updateComplete.then(() => {
        this.open = true
      })
    }
  }

  connectedCallback() {
    super.connectedCallback()

    const toggle = this.querySelector(`[slot="toggle"]`)
    toggle?.setAttribute('aria-haspopup', 'true')
  }

  render() {
    const activeSubmenu = this.submenuStack[this.submenuStack.length - 1]

    return html`
      <div class="n-dropdown" @nord-submenu-navigate=${this.handleSubmenuNavigate} @focusout=${this.handleBlur}>
        <slot name="toggle" aria-controls="popout"></slot>
        <nord-popout
          id="popout"
          align=${ifDefined(this.align)}
          position=${ifDefined(this.position)}
          ?open=${this.open}
          ?always-floating=${this.alwaysFloating}
          @open=${this.handleOpen}
          @close=${this.handleClose}
        >
          ${
            this.isShowingSubmenu
              ? html`
              <div class="n-dropdown-header is-navigation-header">
                <nord-dropdown-item @click=${this.handleBackClick} class="n-dropdown-back-item">
                  <nord-icon slot="start" name="arrow-left" size="s"></nord-icon>
                  ${activeSubmenu?.label ?? ''}
                </nord-dropdown-item>
              </div>
            `
              : html`
              <div class="n-dropdown-header" ?hidden=${this.headerSlot.isEmpty && this.headerEndSlot.isEmpty}>
                <slot name=${this.headerSlot.slotName}></slot>
                <slot name=${this.headerEndSlot.slotName}></slot>
              </div>
            `
          }
          <div class="n-dropdown-content ${this.isShowingSubmenu ? 'is-submenu-active' : ''}">
            <slot></slot>
          </div>
        </nord-popout>
      </div>
    `
  }

  private handleBlur(e: FocusEvent) {
    const relatedTarget = e.relatedTarget as HTMLElement

    // Don't close during navigation or if we're still showing a submenu
    // (the blur event fires after back button is removed from DOM, but we're still in submenu mode)
    if (this.isNavigating || this.isShowingSubmenu)
      return

    // Safari will set relatedTarget to null when clicking on the trigger button
    // because it doesn't focus buttons on click.
    // this caused weird behavior where the dropdown closed _and_ opened with a single click.
    // so we only run this logic if relatedTarget is set, which sidesteps this issue
    if (!relatedTarget) {
      return
    }

    // Close dropdown if focus moved outside
    if (!this.contains(relatedTarget)) {
      this.popout.hide(false)
    }
  }

  private handleOpen() {
    this.open = true
  }

  private handleBackClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()

    this.isNavigating = true
    this.navigateBack()

    // Reset navigation flag after updates complete
    this.updateComplete.then(() => {
      this.isNavigating = false
      // Focus the trigger of the submenu we're returning to, or the first item
      const newTop = this.submenuStack[this.submenuStack.length - 1]
      const focusTarget = newTop
        ? newTop.querySelector<HTMLElement>('[slot="trigger"]')
        : this.querySelector<HTMLElement>('nord-dropdown-item')
      focusTarget?.focus()
    })
  }

  private handleClose(e: Event) {
    // Don't close during navigation
    if (this.isNavigating)
      return

    // Clear submenu stack on close
    this.clearSubmenuStack()

    // Ignore close events from child popouts (submenus)
    // The event target will be a nord-popout element that is a child of this dropdown
    const eventTarget = e.target as HTMLElement
    const isChildPopout = eventTarget?.tagName === 'NORD-POPOUT' && this.contains(eventTarget)

    if (isChildPopout) {
      return
    }

    this.open = false
  }

  @observe('open', 'updated')
  protected handleOpenChange() {
    if (this.open) {
      const focusTarget
        = this.querySelector<HTMLElement>('[autofocus]')
          ?? this.querySelector<HTMLElement>('nord-dropdown-item, nord-message')

      focusTarget?.focus()
      this.list.scrollTop = 0
    }
    else if (!this.isNavigating) {
      // Clear submenu stack when dropdown closes (but not during navigation)
      this.clearSubmenuStack()
    }
  }

  /**
   * Navigate back one level in the submenu stack.
   * @internal
   */
  navigateBack() {
    if (this.submenuStack.length === 0)
      return

    const topSubmenu = this.submenuStack[this.submenuStack.length - 1]
    topSubmenu.deactivateMobile()
    // Trigger re-render so it loses active and is-deepest-active classes
    topSubmenu.requestUpdate()

    const newTop = this.submenuStack[this.submenuStack.length - 2]
    if (newTop) {
      newTop.removeAttribute('has-active-child')
      // Trigger re-render so it gains is-deepest-active class back
      newTop.requestUpdate()
    }

    this.submenuStack = this.submenuStack.slice(0, -1)
  }

  /**
   * Clear the entire submenu stack.
   * @internal
   */
  private clearSubmenuStack() {
    for (const submenu of this.submenuStack) {
      submenu.deactivateMobile()
      submenu.removeAttribute('has-active-child')
    }
    this.submenuStack = []
  }

  /**
   * Handle submenu navigation event.
   * @internal
   */
  private handleSubmenuNavigate(event: Event) {
    const customEvent = event as CustomEvent<{ submenu: DropdownSubmenu }>
    customEvent.stopPropagation()

    const previousDeepest = this.submenuStack[this.submenuStack.length - 1]
    if (previousDeepest) {
      previousDeepest.setAttribute('has-active-child', '')
      // Trigger re-render so it loses is-deepest-active class
      previousDeepest.requestUpdate()
    }

    const newSubmenu = customEvent.detail.submenu
    this.submenuStack = [...this.submenuStack, newSubmenu]

    // Trigger re-render of new deepest so it gains is-deepest-active class
    this.updateComplete.then(() => {
      newSubmenu.requestUpdate()
    })
  }

  /**
   * Hide the dropdown programmatically.
   * This method delegates to the Dropdown's internal Popout component.
   * See [Popout's hide() method](/components/popout#methods-hide) for more details.
   * @param moveFocusToButton A boolean option to move the focus to the original button that opens the dropdown.
   */
  hide(moveFocusToButton?: boolean): Promise<TransitionEvent | void> {
    return this.popout.hide(moveFocusToButton)
  }

  /**
   * Show the dropdown programmatically.
   * This method delegates to the Dropdown's internal Popout component.
   * See [Popout's show() method](/components/popout#methods-hide) for more details.
   */
  show(): Promise<TransitionEvent | void> {
    return this.popout.show()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-dropdown': Dropdown
  }
}
